import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import ChatBox from '../components/ChatBox';
import { LogOut, Users, TrendingUp, CheckCircle, Clock, Building2, Eye, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stage names for display
const STAGE_NAMES = [
  "Consultation",
  "Documents Submission",
  "University & Course Selection",
  "Offer Letters",
  "Deposit Payment",
  "Visa Medical Test",
  "Financial Documents",
  "Credibility Interview",
  "Pre-CAS Submission",
  "CAS Letter",
  "Visa Application"
];

const AgentDashboard = () => {
  const { user, logout } = useAuth();
  const [agentProfile, setAgentProfile] = useState(null);
  const [students, setStudents] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showStudentDetails, setShowStudentDetails] = useState(false);
  const [expandedStages, setExpandedStages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAgentData();
  }, []);

  const fetchAgentData = async () => {
    try {
      const [profileRes, studentsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/agent/profile`),
        axios.get(`${API_URL}/api/agent/students`),
        axios.get(`${API_URL}/api/agent/dashboard-stats`)
      ]);
      
      setAgentProfile(profileRes.data);
      setStudents(studentsRes.data);
      setDashboardStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const toggleStageExpanded = (stageIndex) => {
    setExpandedStages(prev => ({
      ...prev,
      [stageIndex]: !prev[stageIndex]
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-emerald-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_edujourney-29/artifacts/h5igihub_britsfly.png" 
              alt="BritsFly Logo" 
              className="h-10 w-auto"
            />
            <span className="font-heading font-bold text-2xl text-foreground">BritsFly</span>
            <span className="ml-2 px-3 py-1 bg-emerald-100 text-emerald-800 text-xs font-semibold rounded-full">
              Agent Portal
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="font-heading font-semibold text-foreground">{agentProfile?.full_name}</p>
              <p className="text-sm text-muted-foreground">{agentProfile?.company_name || 'Agent'}</p>
            </div>
            <Button
              data-testid="logout-btn"
              onClick={logout}
              variant="outline"
              size="icon"
              className="rounded-full"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading font-bold text-4xl text-foreground mb-2">
            Agent Dashboard
          </h1>
          <p className="text-muted-foreground text-lg">Track your referred students' application progress</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Total Referrals</p>
                  <p className="text-4xl font-bold">{dashboardStats?.total_referrals || 0}</p>
                </div>
                <Users className="h-12 w-12 text-blue-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">Active Students</p>
                  <p className="text-4xl font-bold">{dashboardStats?.active_students || 0}</p>
                </div>
                <TrendingUp className="h-12 w-12 text-emerald-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Completed</p>
                  <p className="text-4xl font-bold">{dashboardStats?.completed_applications || 0}</p>
                </div>
                <CheckCircle className="h-12 w-12 text-purple-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm">Commission Rate</p>
                  <p className="text-4xl font-bold">{dashboardStats?.commission_rate || 0}%</p>
                </div>
                <Building2 className="h-12 w-12 text-amber-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Student List */}
          <div className="lg:col-span-7">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading font-semibold text-2xl">Your Referred Students</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {students.length === 0 ? (
                    <div className="text-center py-12">
                      <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No students assigned yet</p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Students will appear here once a consultant assigns them to you
                      </p>
                    </div>
                  ) : (
                    students.map((student) => {
                      const completedStages = student.stages?.filter(s => s.status === 'completed').length || 0;
                      const progressPercentage = (completedStages / 11) * 100;
                      const isInactive = student.record_status && student.record_status !== 'active';

                      return (
                        <div
                          key={student.id}
                          data-testid={`agent-student-card-${student.id}`}
                          onClick={() => setSelectedStudent(student)}
                          className={`p-5 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                            selectedStudent?.id === student.id
                              ? 'border-emerald-500 bg-emerald-50'
                              : isInactive
                              ? 'border-muted bg-muted/30 opacity-70'
                              : 'border-border hover:border-emerald-300 hover:shadow-md'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="font-heading font-semibold text-lg text-foreground">
                                {student.full_name}
                              </h3>
                              <p className="text-sm text-muted-foreground">{student.email}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {isInactive ? (
                                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-gray-200 text-gray-600">
                                  {student.record_status.replace(/_/g, ' ')}
                                </span>
                              ) : (
                                <span className="text-xs font-bold uppercase px-3 py-1 rounded-full bg-emerald-100 text-emerald-800">
                                  Stage {(student.current_stage || 0) + 1}/11
                                </span>
                              )}
                            </div>
                          </div>

                          {!isInactive && (
                            <>
                              <div className="mb-2">
                                <p className="text-sm font-medium text-foreground">
                                  {STAGE_NAMES[student.current_stage || 0]}
                                </p>
                              </div>
                              <div className="space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-muted-foreground">Progress</span>
                                  <span className="font-semibold text-foreground">{Math.round(progressPercentage)}%</span>
                                </div>
                                <div className="w-full bg-muted rounded-full h-2">
                                  <div
                                    className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${progressPercentage}%` }}
                                  />
                                </div>
                              </div>
                            </>
                          )}

                          {student.country && (
                            <p className="text-xs text-muted-foreground mt-2">From: {student.country}</p>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Selected Student Details */}
            {selectedStudent && (
              <Card className="mt-6">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading font-semibold text-2xl">
                      Application Progress
                    </CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowStudentDetails(true)}
                      className="rounded-full"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Full Details
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {selectedStudent.stages?.map((stage, index) => (
                      <div
                        key={index}
                        className="border rounded-xl overflow-hidden"
                      >
                        <div
                          className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50"
                          onClick={() => toggleStageExpanded(index)}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              stage.status === 'completed' ? 'bg-emerald-500 text-white' :
                              stage.status === 'in_progress' ? 'bg-blue-500 text-white' :
                              'bg-gray-200 text-gray-600'
                            }`}>
                              {index + 1}
                            </div>
                            <div>
                              <p className="font-medium text-foreground">{stage.name}</p>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(stage.status)}`}>
                                {stage.status || 'pending'}
                              </span>
                            </div>
                          </div>
                          {expandedStages[index] ? (
                            <ChevronUp className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                        
                        {expandedStages[index] && (
                          <div className="px-4 pb-4 bg-muted/30">
                            <div className="pt-3 border-t space-y-2">
                              {stage.notes && (
                                <p className="text-sm text-muted-foreground">
                                  <span className="font-medium">Notes:</span> {stage.notes}
                                </p>
                              )}
                              {stage.completed_at && (
                                <p className="text-sm text-muted-foreground">
                                  <span className="font-medium">Completed:</span> {new Date(stage.completed_at).toLocaleDateString()}
                                </p>
                              )}
                              {!stage.notes && !stage.completed_at && (
                                <p className="text-sm text-muted-foreground italic">No additional details available</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Chat Section */}
          <div className="lg:col-span-5">
            {selectedStudent ? (
              <div className="sticky top-6">
                <ChatBox 
                  studentId={selectedStudent.id} 
                  currentUserRole="agent"
                  studentName={selectedStudent.full_name}
                />
              </div>
            ) : (
              <Card>
                <CardContent className="py-20 text-center">
                  <MessageSquare className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Select a student to view chat</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    You can message your referred students to support their applications
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Recent Updates Section */}
        {dashboardStats?.recent_updates?.length > 0 && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="font-heading font-semibold text-xl flex items-center gap-2">
                <Clock className="h-5 w-5 text-muted-foreground" />
                Recent Updates
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboardStats.recent_updates.map((update, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium text-foreground">{update.student_name}</p>
                      <p className="text-sm text-muted-foreground">
                        Currently at: {update.stage_name}
                      </p>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full ${
                      update.record_status === 'active' 
                        ? 'bg-emerald-100 text-emerald-800' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {update.record_status === 'active' ? 'Active' : update.record_status.replace(/_/g, ' ')}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Student Details Dialog */}
      <Dialog open={showStudentDetails} onOpenChange={setShowStudentDetails}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold text-2xl">
              Student Details
            </DialogTitle>
          </DialogHeader>
          {selectedStudent && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Full Name</p>
                  <p className="font-medium">{selectedStudent.full_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{selectedStudent.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{selectedStudent.phone || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Country</p>
                  <p className="font-medium">{selectedStudent.country || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Current Stage</p>
                  <p className="font-medium">{STAGE_NAMES[selectedStudent.current_stage || 0]}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <p className="font-medium capitalize">{selectedStudent.record_status || 'Active'}</p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Application Journey</h4>
                <div className="space-y-2">
                  {selectedStudent.stages?.map((stage, index) => (
                    <div key={index} className="flex items-center gap-3 p-2 rounded-lg bg-muted/30">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        stage.status === 'completed' ? 'bg-emerald-500 text-white' :
                        stage.status === 'in_progress' ? 'bg-blue-500 text-white' :
                        'bg-gray-200 text-gray-600'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{stage.name}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(stage.status)}`}>
                        {stage.status || 'pending'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgentDashboard;
